const debounce = (fn, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};

["load", "scroll", "resize"].forEach((eventName) => {
  window.addEventListener(eventName, (e) => {
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`
    );
  });
});

const bodyScroll = {
  lock(container) {
    bodyScrollLock.disableBodyScroll(container);
  },
  unlock(container) {
    bodyScrollLock.enableBodyScroll(container);
  },
  clear() {
    bodyScrollLock.clearAllBodyScrollLocks();
  },
};

function toggleLock(force, container) {
  return force ? bodyScroll.lock(container) : bodyScroll.clear();
}

const onKeyUpEscape = (event) => {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
};

const getFocusableElements = (container) => {
  const selectors = [
    "summary",
    "a[href]",
    "button:enabled",
    '[tabindex]:not([tabindex^="-"])',
    "[draggable]",
    "area",
    "input:not([type=hidden]):enabled",
    "select:enabled",
    "textarea:enabled",
    "object",
    "iframe",
  ];
  return Array.from(container.querySelectorAll(selectors.join(",")));
};

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute(
    "aria-expanded",
    summary.parentNode.hasAttribute("open")
  );

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open")
    );
  });

  if (summary.closest("header-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

const removeTrapFocus = (elementToFocus = null) => {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
};

const trapFocus = (container, elementToFocus = container) => {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();
};

const serializeForm = (form) => {
  const obj = {};
  const formData = new FormData(form);
  for (const key of formData.keys()) {
    obj[key] = formData.get(key);
  }
  return JSON.stringify(obj);
};

const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

const handleize = (str) => str.replace(/[ /_]/g, "-").toLowerCase();

const decode = (str) => decodeURIComponent(str).replace(/\+/g, " ");

const getOffsetTop = (element) => {
  let offsetTop = 0;

  do {
    if (!isNaN(element.offsetTop)) {
      offsetTop += element.offsetTop;
    }
  } while ((element = element.offsetParent));

  return offsetTop;
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(this.mainDetailsToggle.querySelector("summary"))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const isOpen = detailsElement.hasAttribute("open");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling);
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(summaryElement)
        : this.openMenuDrawer(summaryElement);
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        summaryElement.nextElementSibling.addEventListener(
          "transitionend",
          addTrapFocus
        );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    bodyScroll.lock(summaryElement.nextElementSibling);
  }

  closeMenuDrawer(elementToFocus = false) {
    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    bodyScroll.unlock(
      this.mainDetailsToggle.querySelector("summary").nextElementSibling
    );
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary")
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*"
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

const fetchConfig = (type = "json") => {
  return {
    method: "POST",
    headers: {
      "Content-Type": `application/${type}`,
      Accept: `application/${type}`,
    },
  };
};

class QuantityInput extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });

    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  onButtonClick(event) {
    event.preventDefault();

    const previousValue = this.input.value;

    event.target.name === "increment"
      ? this.input.stepUp()
      : this.input.stepDown();

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);
  }
}

customElements.define("quantity-input", QuantityInput);

class ModalOpener extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const button = this.querySelector("button");

    if (!button) return;

    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));

      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class ModalDialog extends HTMLElement {
  constructor() {
    super();

    this.dialogHolder = this.querySelector('[role="dialog"]');
    this.scrollWrapper = this.dialogHolder;
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this, false)
    );
    this.addEventListener("keyup", (event) => {
      if (event.code?.toUpperCase() === "ESCAPE") this.hide();
    });
    this.addEventListener("click", (event) => {
      if (event.target === this) this.hide();
    });
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    bodyScroll.lock(this.scrollWrapper);
    this.setAttribute("open", "");
    trapFocus(this, this.dialogHolder);
    window.pauseAllMedia();
  }

  hide() {
    bodyScroll.unlock(this.scrollWrapper);
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(
        this.querySelector("template").content.firstElementChild.cloneNode(true)
      );

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(
        content.querySelector(".deferred-media__wrapper")
      );
      if (focus) deferredElement.focus();
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

/**
 * Set cookie.
 * @param {String} name
 * @param {String} value
 * @param {Number} days
 * @returns Void
 */
const setCookie = (name, value, days) => {
  var expires = "";
  if (days) {
    var date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + (value || "") + expires + "; path=/";
};

/**
 * Get cookie data.
 * @param {String} name
 * @returns {String|Null}|
 */
const getCookie = (name) => {
  var nameEQ = name + "=";
  var ca = document.cookie.split(";");
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) == " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

/**
 * Erase cookie value
 * @param {String} name
 */
const eraseCookie = (name) => {
  document.cookie = name + "=; Max-Age=-99999999";
};

/**
 * Init Accordion
 */
const initAccordion = function () {
  const accordionSections = document.querySelectorAll(
    ".js-accordion .accordion__section .accordion__head"
  );

  accordionSections.forEach((section) => {
    section.addEventListener("click", () => {
      const isCurrent = section.parentElement.classList.contains("is-current");

      if (!isCurrent) {
        section.parentElement.classList.add("is-current");
      } else {
        section.parentElement.classList.remove("is-current");
      }
    });
  });
};

window.addEventListener("shopify:section:load", (event) => {
  initAccordion();
});

window.addEventListener("DOMContentLoaded", (event) => {
  initAccordion();
});

/**
 *
 * @param {Node} element Determine if an element is in viewport
 * @returns
 */
const isInView = (element) => {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};
$(document).ready(function () {
  const $quantityInput = $(".quantity__input");
  const $priceSpan = $(".mak-actual-price"); // Actual price display
  const $discountedPriceSpan = $(".mak-discounted_price"); // Discounted price display
  const $priceRegularDiv = $(".price__regular");

  const originalPrice = parseFloat($priceSpan.text().replace("$", "")); // Base price
  const discountPercentage =
    parseFloat($discountedPriceSpan.attr("discount-percentage")) || 10; // Default 10% if not set
  const discountThreshold = 3; // Quantity where discount activates

  // Pre-calculate the discounted unit price
  const discountedUnitPrice =
    originalPrice - originalPrice * (discountPercentage / 100);

  function updatePrices() {
    const quantity = parseInt($quantityInput.val(), 10);

    if (quantity >= discountThreshold) {
      // After threshold reached
      $priceSpan.html("$" + originalPrice.toFixed(2) + ""); // Show strikethrough on original price
      $discountedPriceSpan.text("$" + discountedUnitPrice.toFixed(2)); // Show discounted price
      $priceRegularDiv.addClass("mak__price-discount");
    } else {
      // Before threshold
      $priceSpan.text("$" + originalPrice.toFixed(2)); // Show original unit price
      $discountedPriceSpan.text(""); // Hide discounted price
      $priceRegularDiv.removeClass("mak__price-discount");
    }
  }

  // Attach listeners
  $quantityInput.on("input", updatePrices);
  $('[name="decrement"], [name="increment"]').on("click", updatePrices);

  // Run once on page load
  updatePrices();
});

$(document).ready(function () {
  const $ecoQuantityInput = $(".quantity__input");
  const $ecoPriceSpan = $(".mak-actual-price-btn"); // Select the span where price is displayed
  const $ecoDiscountedPriceSpan = $(".mak-discounted_price-btn"); // Select the span for discounted price
  const $ecoPriceRegularDiv = $(".mak_btn-prices"); // Select the div containing the regular price
  const ecoOriginalPrice = parseFloat($ecoPriceSpan.text().replace("$", "")); // Get the original price (remove the dollar sign)
  const ecoDiscountPercentage = parseFloat(
    $ecoDiscountedPriceSpan.attr("discount-percentage")
  ); // Get the discount percentage
  const ecoThreshold = parseInt(
    $ecoQuantityInput.attr("quantity-threshold"),
    10
  ); // Get the threshold value

  // Function to update the prices
  function updateEcoPrices(ecoCurrentValue) {
    const ecoMultipliedPrice = ecoCurrentValue * ecoOriginalPrice; // Calculate the total price
    const ecoDiscountAmount =
      ecoMultipliedPrice * (ecoDiscountPercentage / 100); // Calculate the discount
    const ecoDiscountedPrice = ecoMultipliedPrice - ecoDiscountAmount; // Apply the discount

    if (ecoCurrentValue === 2) {
      // Special case for quantity 2: show original price × 2 without discount
      const ecoMultipliedPrice = ecoOriginalPrice * 2;
      $ecoPriceSpan.text("$" + ecoMultipliedPrice.toFixed(2));
      $ecoDiscountedPriceSpan.text("");
      $ecoPriceRegularDiv.removeClass("mak__price-discount");
    } else if (ecoCurrentValue >= ecoThreshold) {
      // Normal case for threshold or higher
      $ecoPriceSpan.text("$" + ecoMultipliedPrice.toFixed(2));
      $ecoDiscountedPriceSpan.text("$" + ecoDiscountedPrice.toFixed(2));
      $ecoPriceRegularDiv.addClass("mak__price-discount");
    } else if (ecoCurrentValue === 1) {
      // Reset to original price for quantity 1
      $ecoPriceSpan.text("$" + ecoOriginalPrice.toFixed(2));
      $ecoDiscountedPriceSpan.text("");
      $ecoPriceRegularDiv.removeClass("mak__price-discount");
    }
  }

  // Function to check quantity and update UI
  function ecoCheckQuantity() {
    const ecoCurrentValue = parseInt($ecoQuantityInput.val(), 10); // Get the current value from the input
    updateEcoPrices(ecoCurrentValue);
  }

  // Listen for changes to the quantity input (including user input and clicking increment/decrement)
  $ecoQuantityInput.on("input", function () {
    ecoCheckQuantity();
  });

  // Listen for click on decrement button
  $('[name="decrement"]').on("click", function () {
    setTimeout(ecoCheckQuantity, 10); // Small delay to ensure value is updated
  });

  // Listen for click on increment button
  $('[name="increment"]').on("click", function () {
    setTimeout(ecoCheckQuantity, 10); // Small delay to ensure value is updated
  });

  // Initialize on page load
  ecoCheckQuantity();
});

/* --- SWIPER / YOUTUBE CLS & CONSOLE SPAM FIX --- */
// Wait for Swiper to finish creating its duplicate slides
window.addEventListener('load', function() {
  
  // 1. Find all YouTube iframes inside Swiper's cloned slides
  const clonedIframes = document.querySelectorAll('.swiper-slide-duplicate iframe[src*="youtube.com"]');
  
  clonedIframes.forEach(function(iframe) {
    // 2. Turn off the JS API on the clones so widgetapi.js ignores them
    if (iframe.src.includes('enablejsapi=1')) {
      iframe.src = iframe.src.replace('enablejsapi=1', 'enablejsapi=0');
    }
    
    // 3. Remove the class so your theme's pauseAllMedia() function ignores them too
    iframe.classList.remove('js-youtube');
  });

});
